import React, { useState, useEffect, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseconfig';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];
const RADIAN = Math.PI / 180;

const AreaBudgetPieChart = ({ formatCurrency }) => {
  const [areasData, setAreasData] = useState({});
  const [expandedAreas, setExpandedAreas] = useState({});
  const [chartSize, setChartSize] = useState({ width: 300, height: 300 });
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAreasData = async () => {
      try {
        const budgetsCollection = collection(db, 'budgets');
        const querySnapshot = await getDocs(budgetsCollection);
        const budgets = querySnapshot.docs
          .filter(doc => doc.id !== 'metadata')
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

        const areas = budgets.filter(budget => budget.type === 'area');
        const subAreas = budgets.filter(budget => budget.type === 'subarea');

        const fetchedAreasData = {};
        for (const area of areas) {
          const areaSubAreas = subAreas.filter(subArea => subArea.from === area.id);
          const subAreasData = {};

          for (const subArea of areaSubAreas) {
            const subAreaCollection = collection(db, subArea.id);
            const subAreaSnapshot = await getDocs(subAreaCollection);
            const obras = subAreaSnapshot.docs
              .filter(doc => doc.id !== 'metadata')
              .map(doc => ({
                name: doc.data().obra,
                value: doc.data().costo || 0
              }));

            const usedBudget = obras.reduce((sum, obra) => sum + obra.value, 0);
            const remainingBudget = Math.max(subArea.amount - usedBudget, 0);

            subAreasData[subArea.id] = {
              name: subArea.id,
              amount: subArea.amount,
              obras,
              usedBudget,
              remainingBudget
            };
          }

          const areaCollection = collection(db, area.id);
          const areaSnapshot = await getDocs(areaCollection);
          const areaProjects = areaSnapshot.docs
            .filter(doc => doc.id !== 'metadata')
            .map(doc => ({
              name: doc.data().obra,
              value: doc.data().costo || 0
            }));

          fetchedAreasData[area.id] = {
            name: area.id,
            amount: area.amount,
            subAreas: subAreasData,
            used: area.used || 0,
            usedarea: area.usedarea || 0,
            projects: areaProjects
          };
        }

        setAreasData(fetchedAreasData);
      } catch (error) {
        console.error("Error fetching areas data:", error);
      }
    };

    fetchAreasData();
  }, []);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        const size = Math.min(width, 400);
        setChartSize({ width: size, height: size });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const handleBudgetClick = (area, subArea) => {
    navigate('/admin/control', { state: { selectedArea: area, specificArea: subArea || area } });
  };

  const toggleExpanded = (areaId) => {
    setExpandedAreas(prev => ({
      ...prev,
      [areaId]: !prev[areaId]
    }));
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        style={{
          fontSize: '12px',
          fontWeight: 'bold',
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
          stroke: "grey",
          strokeWidth: "1px",
          paintOrder: "stroke"
        }}
      >
        {`${(percent * 100).toFixed(2)}%`}
      </text>
    );
  };

  const mainChartData = Object.entries(areasData).map(([areaId, areaData]) => ({
    name: areaData.name,
    value: areaData.amount
  }));

  const totalBudget = mainChartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="areas-detail-charts" ref={containerRef}>
      <div className='container-invisible'>
        <div className="main-area-chart">
          <h3 className='areaName'>Distribución de Presupuesto por Áreas</h3>
          <div className="chart-content">
          <div className="pie-container" style={{ width: '100%', height: chartSize.height }}>
            <ResponsiveContainer className="res-container">
              <p className='totalBudget'>Presupuesto Total: {formatCurrency(totalBudget)}</p>
              <PieChart>
                <Pie
                  data={mainChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={chartSize.width / 4}
                  innerRadius={chartSize.width / 6}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {mainChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const item = payload[0].payload;
                      const percentage = (item.value / totalBudget) * 100;
                      return (
                        <div className='area-hover'>
                          <p>{item.name}</p>
                          <p>{formatCurrency(item.value)} ({percentage.toFixed(2)}%)</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend 
                  formatter={(value, entry) => {
                    const item = mainChartData.find(d => d.name === value);
                    const percentage = (item.value / totalBudget) * 100;
                    return `${value} ${formatCurrency(item.value)} (${percentage.toFixed(2)}%)`;
                  }}
                  wrapperStyle={{ fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          </div>
        </div>
      </div>
      {Object.entries(areasData).map(([areaId, areaData]) => {
        const totalUsed = areaData.used || 0;
        const remaining = Math.max(areaData.amount - totalUsed, 0);

        const areaChartData = [
          { name: areaData.name, value: areaData.usedarea, isArea: true },
          ...Object.entries(areaData.subAreas).map(([name, data]) => ({
            name,
            value: data.usedBudget || 0,
            isArea: false
          })),
          { name: 'Restante', value: remaining, isArea: false }
        ];

        return (
          <div key={areaId} className={`area-detail-chart ${expandedAreas[areaId] ? 'expanded' : ''}`}>
            <div className="areaName-container">
              <h3 className='areaName'>{areaData.name}</h3>
              <p className='totalBudget'>Presupuesto Total: {formatCurrency(areaData.amount)}</p>
            </div>
            <div className="chart-content">
              <div className="pie-container" style={{ width: '100%', height: chartSize.height }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={areaChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={chartSize.width / 4}
                      innerRadius={chartSize.width / 6}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {areaChartData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.isArea ? '#FF0000' : COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const item = payload[0].payload;
                          const percentage = (item.value / areaData.amount) * 100;
                          return (
                            <div className='area-hover'>
                              <p>{item.name}{item.isArea ? ' (Área)' : ''}</p>
                              <p>{formatCurrency(item.value)} ({percentage.toFixed(2)}%)</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend 
                      className='legend'
                      formatter={(value, entry) => {
                        const item = areaChartData.find(d => d.name === value);
                        const percentage = (item.value / areaData.amount) * 100;
                        return `${value}${item.isArea ? ' (Área)' : ''} ${formatCurrency(item.value)} (${percentage.toFixed(2)}%)`;
                      }}
                      wrapperStyle={{ fontSize: '12px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="buttons">
                <button 
                  onClick={() => toggleExpanded(areaId)} 
                  className="btn-expand-control"
                >
                  {expandedAreas[areaId] ? 'Contraer' : 'Expandir'}
                </button>
                <button 
                  onClick={() => handleBudgetClick(areaId)} 
                  className="btn-details-control"
                >
                  Ver detalles
                </button>
              </div>
            </div>
            {expandedAreas[areaId] && (
              <div className="expanded-charts">
                <div className="chart-content">
                  <h4 className='areaName'>{areaData.name} (Proyectos)</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[...areaData.projects, { name: 'Restante', value: Math.max(areaData.amount - totalUsed, 0) }]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={100}
                        innerRadius={60}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {areaData.projects.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                        <Cell key="cell-remaining" fill="#CCCCCC" />
                      </Pie>
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const item = payload[0].payload;
                            const percentage = (item.value / areaData.amount) * 100;
                            return (
                              <div className='area-hover'>
                                <p>{item.name}</p>
                                <p>{formatCurrency(item.value)} ({percentage.toFixed(2)}%)</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend 
                        formatter={(value, entry) => {
                          const item = [...areaData.projects, { name: 'Restante', value: Math.max(areaData.amount - totalUsed, 0) }].find(d => d.name === value);
                          const percentage = (item.value / areaData.amount) * 100;
                          return `${value} ${formatCurrency(item.value)} (${percentage.toFixed(2)}%)`;
                        }}
                        wrapperStyle={{ fontSize: '12px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {Object.entries(areaData.subAreas).map(([subAreaId, subAreaData]) => (
                  <div key={subAreaId} className="chart-content">
                    <h4 className='areaName'>{subAreaData.name}</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[...subAreaData.obras, { name: 'Restante', value: subAreaData.remainingBudget }]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={renderCustomizedLabel}
                          outerRadius={100}
                          innerRadius={60}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {subAreaData.obras.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                          <Cell key="cell-remaining" fill="#CCCCCC" />
                        </Pie>
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const item = payload[0].payload;
                              const percentage = (item.value / subAreaData.amount) * 100;
                              return (
                                <div className='area-hover'>
                                  <p>{item.name}</p>
                                  <p>{formatCurrency(item.value)} ({percentage.toFixed(2)}%)</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend 
                          formatter={(value, entry) => {
                            const item = [...subAreaData.obras, { name: 'Restante', value: subAreaData.remainingBudget }].find(d => d.name === value);
                            const percentage = (item.value / subAreaData.amount) * 100;
                            return `${value} ${formatCurrency(item.value)} (${percentage.toFixed(2)}%)`;
                          }}
                          wrapperStyle={{ fontSize: '12px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="border-btn">
                      <button 
                        onClick={() => handleBudgetClick(areaId, subAreaId)} 
                        className="detail-subarea"
                      >
                        Ver detalles
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AreaBudgetPieChart;

